// pages/ServicingDashboard/ServicingDashboard.jsx
// REFACTORED - Using shared utilities and constants

import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/common/Toast';
import { ToolTip} from '../../components/common/ToolTip';
import { useCycleData } from '../../hooks/client/useCycleData';
import { useCustomerBreakdown } from '../../hooks/client/useCustomerBreakdown';
import { useLastUpdated } from '../../hooks/client/useLastUpdated';

import { Header } from '../../components/client_servicing/Header';
import CycleSelector from '../../components/client_servicing/sections/CycleSelector';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import CollapsibleSection from '../../components/common/CollapsibleSection';
import { LoadingSpinner, ErrorMessage, NoDataMessage } from '../../components/common';
import OverServicing from '../../components/client_servicing/sections/OverServicing';
import CustomerBreakdown from '../../components/client_servicing/sections/CustomerBreakdown';
import { exportUtils } from '../../utils/dataProcessors';

// Import shared utilities and constants
import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';

// ============================================================================
// MEMOIZED COMPONENTS
// ============================================================================

// Cycle Definitions Tooltip Component
const CycleDefinitionsTooltip = () => {
  const KPI_DEFINITIONS = {
    "Current Cycle": "Shows the current billing period in progress. For 1st cycle: from the 1st of the month to today. For 15th cycle: from the 15th of last month to today (if before the 15th), or from the 15th to today (if on or after the 15th).",
    "Last Completed Cycle": "Shows the most recently completed billing period. For 1st cycle: the full period from 1st to end of last month. For 15th cycle: the full period from 15th of two months ago to 14th of last month."
  };

  return <ToolTip definitions={KPI_DEFINITIONS} width="w-96" />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ClientServicingDashboard = () => {
  // Toast notifications
  const { toasts, removeToast } = useToast();
  
  // Cycle navigation state - use shared utility
  const [selectedCycleMonth, setSelectedCycleMonth] = useState(getCurrentCycleMonth);
  const [viewMode, setViewMode] = useState('current');

  // Customer details date range state - use shared utility
  const [customerDateRange, setCustomerDateRange] = useState(getDefaultDateRange);

  // Section collapse state - use shared constant
  const [sectionState, setSectionState] = useState(DEFAULT_SECTION_STATE);

  // ============================================================================
  // DATA HOOKS
  // ============================================================================
  
  // Cycle/Overservicing data
  const {
    overservicingData,
    cycleMetadata,
    loading: cycleLoading,
    refreshing: cycleRefreshing,
    error: cycleError,
    initialized: cycleInitialized,
    refetch: refetchCycle,
  } = useCycleData(viewMode, selectedCycleMonth);

  // Customer breakdown data
  const {
    customerDetailsData,
    customerSummaries,
    summaryStats,
    quarterlyBreakdown,
    quarters,
    hasQuarterlyData,
    loading: customerLoading,
    refreshing: customerRefreshing,
    error: customerError,
    initialized: customerInitialized,
    refetch: refetchCustomer,
  } = useCustomerBreakdown(
    customerDateRange?.start_date, 
    customerDateRange?.end_date,
    customerDateRange?.preset
  );

  // Last updated timestamp
  const { lastUpdated } = useLastUpdated();

  // ============================================================================
  // HANDLERS
  // ============================================================================
  
  // Section state updater
  const updateSectionState = useCallback((key, value) => {
    setSectionState(prev => ({ ...prev, [key]: value }));
  }, []);

  // Cycle navigation
  const navigateMonth = useCallback((direction) => {
    const [year, month] = selectedCycleMonth.split('-').map(Number);
    const currentDate = new Date(year, month - 1, 1);
    
    if (direction === 'prev') {
      currentDate.setMonth(currentDate.getMonth() - 1);
    } else {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    const newCycleMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedCycleMonth(newCycleMonth);
    setViewMode('custom');
  }, [selectedCycleMonth]);

  // View mode toggle
  const toggleViewMode = useCallback((mode) => {
    setViewMode(mode);
    if (mode !== 'custom') {
      setSelectedCycleMonth(getCurrentCycleMonth());
    }
  }, []);

  // Month selector
  const selectMonth = useCallback((monthValue) => {
    setSelectedCycleMonth(monthValue);
    setViewMode('custom');
  }, []);

  // Customer date range change
  const handleCustomerDateRangeChange = useCallback((newDateRange) => {
    setCustomerDateRange(newDateRange);
  }, []);

  // Export handlers
  const handleExportOverservicing = useCallback(async () => {
    try {
      const dataToExport = overservicingData?.client_summaries || [];
      if (dataToExport.length > 0) {
        const headers = [
          'Client Name', 'Client Type', 'Billing Cycle', 'Client Hours', 
          'Billable Incurred', 'Fees Collected', 'Over/Under Service', 'Completion %'
        ];
        const exportData = dataToExport.map(row => ({
          'Client Name': row.client_name || row.customer || '',
          'Client Type': row.client_type || '',
          'Billing Cycle': row.billing_cycle || '',
          'Client Hours': row.client_hours || 0,
          'Billable Incurred': row.billable_incurred || 0,
          'Fees Collected': row.fees_collected || 0,
          'Over/Under Service': row.overservice_amount || 0,
          'Completion %': row.completion_percentage || 0
        }));
        const filename = `overservicing_${selectedCycleMonth}_${viewMode}.csv`;
        exportUtils.downloadCSV(exportData, filename, headers);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [overservicingData, selectedCycleMonth, viewMode]);

  const handleExportCustomer = useCallback(async () => {
    try {
      const dataToExport = customerSummaries || [];
      if (dataToExport.length > 0) {
        const headers = [
          'Customer', 'Client Hours', 'Billable Incurred', 'Invoiced Fees', 
          'Over/Under Service', 'Employee Count'
        ];
        const exportData = dataToExport.map(row => ({
          'Customer': row.customer || '',
          'Client Hours': row.client_hours || 0,
          'Billable Incurred': row.incurred_fees || 0,
          'Invoiced Fees': row.invoiced_fees || 0,
          'Over/Under Service': row.overservice_amount || 0,
          'Employee Count': row.employee_count || 0
        }));
        const filename = `customer_details_${customerDateRange.start_date}_to_${customerDateRange.end_date}.csv`;
        exportUtils.downloadCSV(exportData, filename, headers);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [customerSummaries, customerDateRange]);

  // Global refresh with cache clearing - use shared utility
  const handleGlobalRefresh = useCallback(async () => {
    await refreshDashboardCache(
      CACHE_ENDPOINTS.CLIENT_SERVICING,
      [refetchCycle, refetchCustomer]
    );
  }, [refetchCycle, refetchCustomer]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  
  // Combined loading states
  const loading = cycleLoading || customerLoading;
  const refreshing = cycleRefreshing || customerRefreshing;
  const error = cycleError || customerError;
  const initialized = cycleInitialized || customerInitialized;

  // Labels for cycle selector
  const currentLabel = useMemo(() => {
    return cycleMetadata?.period_label || 'Current Cycle';
  }, [cycleMetadata]);

  const lastCompletedLabel = useMemo(() => {
    const today = new Date();
    const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return prevMonth.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, []);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Set document title and favicon - use shared utility
  useEffect(() => {
    setDocumentMeta(DOCUMENT_TITLES.CLIENT);
  }, []);

  // ============================================================================
  // RENDER GUARDS
  // ============================================================================
  
  // Initial loading state
  if (loading && !initialized) {
    return <LoadingSpinner />;
  }

  // Error state (only if no cached data)
  if (error && !overservicingData && !customerDetailsData) {
    return <ErrorMessage message="Failed to load data" onRetry={handleGlobalRefresh} />;
  }

  // No data state
  if (!loading && !overservicingData && !customerDetailsData) {
    return (
      <NoDataMessage 
        title="No Client Servicing Data Available" 
        message="No data found." 
        onRetry={handleGlobalRefresh} 
      />
    );
  }
  
  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  
  return (
    <div className="page-wrapper">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <div className="layout-container responsive-space">
        {/* Header */}
        <Header 
          loading={refreshing}
          lastUpdated={lastUpdated}
          onRefresh={handleGlobalRefresh}
        />

        {/* Main Content */}
        <div className="responsive-space">
          {/* Overservicing Analysis Section */}
          <CollapsibleSection
            title={
              <div className="flex items-center gap-2">
                <span>Overservicing Analysis</span>
                <CycleDefinitionsTooltip />
              </div>
            }
            subtitle="Navigate through billing cycles. Retainers show cycle-specific data, projects show all data."
            collapsed={sectionState.overServicingCollapsed}
            setCollapsed={(value) => updateSectionState('overServicingCollapsed', value)}
            rightContent={
              <div className="flex items-center space-x-2">
                <CycleSelector
                  selectedCycleMonth={selectedCycleMonth}
                  onNavigate={navigateMonth}
                  viewMode={viewMode}
                  onToggleView={toggleViewMode}
                  onMonthSelect={selectMonth}
                  currentLabel={currentLabel}
                  lastCompletedLabel={lastCompletedLabel}
                />
              </div>
            }
          >
            <OverServicing 
              data={overservicingData}
              summaryStats={overservicingData?.summary_stats}
              loading={cycleLoading}
              error={cycleError}
              onRefresh={refetchCycle}
              onExport={handleExportOverservicing}
            />
          </CollapsibleSection>

          {/* Customer Details Section */}
          <CollapsibleSection
            title="Customer Details"
            subtitle="Filter by date range to see customer summaries"
            collapsed={sectionState.customerCollapsed}
            setCollapsed={(value) => updateSectionState('customerCollapsed', value)}
            rightContent={
              <div className="flex items-center space-x-2">
                <DateRangeSelector
                  dateRange={customerDateRange}
                  onDateRangeChange={handleCustomerDateRangeChange}
                />
              </div>
            }
          >  
            <CustomerBreakdown 
              customerFeeData={customerSummaries}
              summaryStats={summaryStats}
              quarterlyBreakdown={quarterlyBreakdown}
              quarters={quarters}
              hasQuarterlyData={hasQuarterlyData}
              dateRangePreset={customerDateRange?.preset}
              overserviceData={{}}
              loading={customerLoading}
              error={customerError}
              onRefresh={refetchCustomer}
              onExport={handleExportCustomer}
            />
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

export default memo(ClientServicingDashboard);